import { api, apiUrl } from './api';
import { authedFetch } from '../hooks/useTelegram';

export interface NotificationPrefs {
  deposits: boolean;
  transfers: boolean;
  cards: boolean;
  security: boolean;
}

export interface MeUser {
  id: string;
  nickname: string | null;
  email: string | null;
  emailVerified: boolean;
  pendingEmail: string | null;
  language: string;
  avatarUrl: string | null;
  pinSet: boolean;
  biometricEnabled: boolean;
  notifications: NotificationPrefs;
}

export interface MeLinks {
  legal: string;
  supportEmail: string;
  supportTelegram: string;
}

export interface FaqText {
  en: string;
  ru: string;
  uk?: string;
  pl?: string;
  es?: string;
  tr?: string;
  el?: string;
  it?: string;
  de?: string;
  pt?: string;
}

export interface FaqItem {
  q: FaqText;
  a: FaqText;
}

export interface MeResponse {
  user: MeUser;
  links: MeLinks;
  faq: FaqItem[];
}

export const fetchMe = (initData: string) => api<MeResponse>(initData, '/v1/me');

export const patchMe = (initData: string, body: { nickname?: string; language?: string }) =>
  api<{ user: MeUser }>(initData, '/v1/me', { method: 'PATCH', body: JSON.stringify(body) });

export const startEmail = (initData: string, email: string) =>
  api<{ ok: boolean; sent: boolean }>(initData, '/v1/me/email/start', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const verifyEmail = (initData: string, code: string) =>
  api<{ user: MeUser }>(initData, '/v1/me/email/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });

export const setPinApi = (initData: string, body: { pin: string; currentPin?: string }) =>
  api<{ ok: boolean; pinSet: boolean }>(initData, '/v1/me/pin', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const removePinApi = (initData: string, reauthToken?: string) =>
  api<{ ok: boolean; pinSet: boolean }>(initData, '/v1/me/pin', {
    method: 'DELETE',
    headers: reauthToken ? { 'X-Reauth': reauthToken } : undefined,
  });

/** Current server-side PIN-lockout state (so screens show the block on mount). */
export const reauthStatus = (initData: string) =>
  api<{ lockedUntil: number }>(initData, '/v1/me/reauth/status');

export type ReauthResult =
  | { ok: true; token: string; expiresIn: number }
  | { ok: false; status: number; error: string; lockedUntil?: number; remaining?: number };

/** Mint a reauth token. Returns a result (doesn't throw) so callers can read the
 *  server-enforced lockout state (429 → lockedUntil, 403 → remaining). */
export async function reauthApi(
  initData: string,
  body: { method: 'pin' | 'biometric'; pin?: string },
): Promise<ReauthResult> {
  const res = await authedFetch(initData, apiUrl('/v1/me/reauth'), {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    token?: string;
    expiresIn?: number;
    error?: string;
    lockedUntil?: number;
    remaining?: number;
  };
  if (res.ok && data.token) return { ok: true, token: data.token, expiresIn: data.expiresIn ?? 0 };
  return {
    ok: false,
    status: res.status,
    error: data.error ?? 'error',
    lockedUntil: data.lockedUntil,
    remaining: data.remaining,
  };
}

/** Store the wrapped seed-encryption key (server-held half).
 *  Pass `reauthToken` when rotating an orphan key (seed_key_exists, no wallet). */
export const storeSeedKey = (initData: string, key: string, reauthToken?: string) =>
  api<{ ok: boolean; created?: boolean; rotated?: boolean }>(initData, '/v1/me/seed-key', {
    method: 'POST',
    headers: reauthToken ? { 'X-Reauth': reauthToken } : undefined,
    body: JSON.stringify({ key }),
  });

/** Fetch the seed-encryption key. Pass an X-Reauth token when a PIN/biometric is set. */
export const getSeedKey = (initData: string, reauthToken?: string) =>
  api<{ key: string }>(initData, '/v1/me/seed-key', {
    headers: reauthToken ? { 'X-Reauth': reauthToken } : undefined,
  });

/** Start wallet-reset OTP (requires reauth + verified email). */
export const startWalletResetApi = (initData: string, reauthToken?: string) =>
  api<{ ok: boolean; sent: boolean; email: string }>(initData, '/v1/me/wallet/reset/start', {
    method: 'POST',
    headers: reauthToken ? { 'X-Reauth': reauthToken } : undefined,
  });

/** Delete the user's backend wallet (+ addresses/seed key) so it re-onboards.
 *  Requires the email OTP from `startWalletResetApi`. */
export const deleteWalletApi = (initData: string, code: string, reauthToken?: string) =>
  api<{ ok: boolean }>(initData, '/v1/me/wallet', {
    method: 'DELETE',
    headers: reauthToken ? { 'X-Reauth': reauthToken } : undefined,
    body: JSON.stringify({ code }),
  });

export const setNotificationsApi = (initData: string, prefs: Partial<NotificationPrefs>) =>
  api<{ notifications: NotificationPrefs }>(initData, '/v1/me/notifications', {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  });

export const setBiometricApi = (initData: string, enabled: boolean, reauthToken?: string) =>
  api<{ ok: boolean; biometricEnabled: boolean }>(initData, '/v1/me/biometric', {
    method: 'PATCH',
    headers: reauthToken ? { 'X-Reauth': reauthToken } : undefined,
    body: JSON.stringify({ enabled }),
  });
